import { FilterGroup } from '@casops/filters/types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export enum FaultLevel {
  One = '1',
  Two = '2',
  Three = '3',
}

export type FaultLevelKey = keyof typeof FaultLevel;
export const faultLevelKeys: FaultLevelKey[] = [...(Object.keys(FaultLevel) as FaultLevelKey[])];

export enum SidePanel {
  Filters,
  ManageUser,
  UserSettings,
  UserChangePassword,
  ManageAsset,
  ManageAssetApproval,
  ManageAssetGroup,
  ManageConfigurationGroup,
  ReadGeofence,
  ManageConfigurationGroupPackage,
  ManageGeofenceArea,
  ManageGeofenceSpeedLimit,
  ManageGeofenceHoldTime,
  ManageGeofenceStyle,
  ManagePersonnelSafety,
  ManagePinDropTemplate,
  CertificateImport,
  LibraryUpload,
  DeviceFileUpload,
  PreApprovalImport,
  Settings,
  AssetTracks,
  AssetsAssignConfigurationGroup,
  GeofenceImport,
  SafetyAlert,
}

export enum SettingsSidePanelTab {
  SiteDetails,
  Localization,
  Map,
}

export enum GeofenceSidePanelTab {
  General,
  Rules,
  InputOutput,
}

interface SidePanelData {
  sidePanelOpened: boolean;
  sidePanel?: SidePanel;
  sidePanelModelId?: string;
  sidePanelBulkModelIds?: string[];
  sidePanelTab?: SettingsSidePanelTab | GeofenceSidePanelTab;
  sidePanelEditMode: boolean;
  sidePanelBulkEditMode: boolean;
  sidePanelFilters?: FilterGroup;
}

export type GlobalData = SidePanelData;

interface ActionOpenBaseModel {
  sidePanel: SidePanelData['sidePanel'];
  tab?: SidePanelData['sidePanelTab'];
  modelId?: SidePanelData['sidePanelModelId'];
  bulkModelIds?: SidePanelData['sidePanelBulkModelIds'];
  editMode?: SidePanelData['sidePanelEditMode'];
  bulkEditMode?: SidePanelData['sidePanelBulkEditMode'];
  filters?: FilterGroup;
}
interface ActionOpenFilters extends ActionOpenBaseModel {
  sidePanel: SidePanel.Filters;
  filters: FilterGroup;
}

export type ActionOpenSidePanel = ActionOpenFilters | ActionOpenBaseModel;

const initialSidePanelData: SidePanelData = {
  sidePanelOpened: false,
  sidePanel: undefined,
  sidePanelTab: undefined,
  sidePanelModelId: undefined,
  sidePanelBulkModelIds: undefined,
  sidePanelEditMode: false,
  sidePanelBulkEditMode: false,
  sidePanelFilters: undefined,
};

const initialData: GlobalData = {
  ...initialSidePanelData,
};

const globalSlice = createSlice({
  name: 'global',
  initialState: initialData,
  reducers: {
    openSidePanel: (state, action: PayloadAction<ActionOpenSidePanel>) => ({
      ...state,
      sidePanel: action.payload.sidePanel,
      sidePanelTab: action.payload.tab,
      sidePanelModelId: action.payload.modelId,
      sidePanelBulkModelIds: action.payload.bulkModelIds,
      sidePanelOpened: true,
      sidePanelFilters: action.payload.filters,
      sidePanelEditMode: action.payload.editMode ?? false,
      sidePanelBulkEditMode: action.payload.bulkEditMode ?? false,
    }),
    closeSidePanel: state => ({
      ...state,
      ...initialSidePanelData,
    }),
  },
});

export const { openSidePanel, closeSidePanel } = globalSlice.actions;

export default globalSlice.reducer;
